import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { BLANK_STRING_LABEL, EMPTY_VALUE_LABEL } from './constants';

interface FieldValueProps {
  value: unknown;
  fieldKey: string;
}

const FieldValue = ({ value, fieldKey }: FieldValueProps) => {
  // Empty values
  if (value === null || value === undefined) {
    return <span className="text-slate-400 italic">{EMPTY_VALUE_LABEL}</span>;
  }

  // Empty strings
  if (value === '') {
    return <span className="text-slate-400 italic">{BLANK_STRING_LABEL}</span>;
  }

  // Booleans - show as icons
  if (typeof value === 'boolean') {
    return (
      <span className={value ? 'text-emerald-600' : 'text-red-600'}>
        <FontAwesomeIcon icon={value ? faCheck : faXmark} className="text-xs" />
      </span>
    );
  }

  // Currency fields (ending in _in_cents)
  if (typeof value === 'number' && fieldKey.endsWith('_in_cents')) {
    const dollars = value / 100;
    return (
      <span className="text-blue-600">
        {value} <span className="text-slate-500">(${dollars.toFixed(2)})</span>
      </span>
    );
  }

  // Numbers
  if (typeof value === 'number') {
    return <span className="text-blue-600">{value}</span>;
  }

  // Strings
  if (typeof value === 'string') {
    // URLs
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return (
        <span>
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800">
            {value}
          </a>
        </span>
      );
    }

    // ISO date strings (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const formatted = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
          return (
            <span className="text-slate-800">
              {value} <span className="text-slate-500">({formatted})</span>
            </span>
          );
        }
      } catch {
        return <span className="text-slate-800">{value}</span>;
      }
    }

    // ISO datetime strings
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const formatted = date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          return (
            <span className="text-slate-800">
              {value} <span className="text-slate-500">({formatted})</span>
            </span>
          );
        }
      } catch {
        return <span className="text-slate-800">{value}</span>;
      }
    }

    // UUIDs — muted monospace so they don't dominate the line
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
      return <span className="font-mono text-xs text-slate-400">{value}</span>;
    }

    return <span className="text-slate-800">{value}</span>;
  }

  return <span className="text-slate-600">{String(value)}</span>;
};

export default FieldValue;
